import React, { useMemo, useState } from 'react';
import CodeExample from './CodeExample';
import { Code, ExpandMore, ExpandLess, ContentCopy, CheckCircle, PlayArrow } from '@mui/icons-material';
import { getAuthUrl, getCliPaymentUrl, BASE_URLS } from '../../data/apiConfig';

const ExampleCard = ({ example, category }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [copied, setCopied] = useState(false);

    // Construire les URLs une fois (flux lien de paiement = cli-payments)
    const authUrl = getAuthUrl();
    const paymentUrl = getCliPaymentUrl();
    const apiBaseUrl = BASE_URLS.api.production;

    // Données d'exemple pour chaque catégorie
    const codeExamples = useMemo(() => ({
        'nodejs-auth': {
            language: 'javascript',
            code: `const axios = require('axios');
const { Buffer } = require('buffer');

// Encodage Base64 des credentials
const credentials = Buffer.from(\`\${process.env.CLIENT_ID}:\${process.env.CLIENT_SECRET}\`).toString('base64');

const getAccessToken = async () => {
  try {
    const response = await axios.post('${authUrl}', 
      { grant_type: 'client_credentials' },
      {
        headers: {
          'Authorization': \`Basic \${credentials}\`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      }
    );
    
    return response.data.access_token;
  } catch (error) {
    console.error('Erreur d\\'authentification:', error.response?.data);
    throw error;
  }
};`
        },
        'ts-auth': {
            language: 'typescript',
            code: `type TokenResponse = {
  access_token: string;
  token_type: string;
  expires_in: number;
};

export async function getAccessToken(): Promise<string> {
  const clientId = process.env.CLIENT_ID!;
  const clientSecret = process.env.CLIENT_SECRET!;
  const credentials = Buffer.from(\`\${clientId}:\${clientSecret}\`).toString('base64');

  const res = await fetch('${authUrl}', {
    method: 'POST',
    headers: {
      Authorization: \`Basic \${credentials}\`,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    },
    body: JSON.stringify({ grant_type: 'client_credentials' })
  });

  if (!res.ok) {
    const body = await res.text();
    throw new Error(\`Auth failed: \${res.status} \${body}\`);
  }

  const data = (await res.json()) as TokenResponse;
  return data.access_token;
}`
        },
        'python-auth': {
            language: 'python',
            code: `import requests
import base64
import os

def get_access_token():
    # Encodage Base64 des credentials
    credentials = f"{os.getenv('CLIENT_ID')}:{os.getenv('CLIENT_SECRET')}"
    encoded_credentials = base64.b64encode(credentials.encode()).decode()
    
    headers = {
        'Authorization': f'Basic {encoded_credentials}',
        'Content-Type': 'application/json'
    }
    
    data = {
        'grant_type': 'client_credentials'
    }
    
    try:
        response = requests.post(
            '${authUrl}',
            headers=headers,
            json=data
        )
        response.raise_for_status()
        return response.json()['access_token']
    except requests.exceptions.RequestException as e:
        print(f"Erreur d'authentification: {e}")
        raise`
        },
        'php-auth': {
            language: 'php',
            code: `<?php
$clientId = getenv('CLIENT_ID');
$clientSecret = getenv('CLIENT_SECRET');

$credentials = base64_encode($clientId . ':' . $clientSecret);
$payload = json_encode(['grant_type' => 'client_credentials']);

$ch = curl_init('${authUrl}');
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => [
    'Authorization: Basic ' . $credentials,
    'Content-Type: application/json',
    'Accept: application/json',
  ],
  CURLOPT_POSTFIELDS => $payload,
]);

$raw = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($status < 200 || $status >= 300) {
  throw new Exception("Auth failed ($status): " . $raw);
}

$data = json_decode($raw, true);
echo $data['access_token'];
?>`
        },
        'go-auth': {
            language: 'go',
            code: `package main

import (
  "bytes"
  "encoding/base64"
  "encoding/json"
  "fmt"
  "io"
  "net/http"
  "os"
)

type tokenResponse struct {
  AccessToken string \`json:"access_token"\`
  TokenType   string \`json:"token_type"\`
  ExpiresIn   int    \`json:"expires_in"\`
}

func main() {
  clientID := os.Getenv("CLIENT_ID")
  clientSecret := os.Getenv("CLIENT_SECRET")
  creds := base64.StdEncoding.EncodeToString([]byte(clientID + ":" + clientSecret))

  body, _ := json.Marshal(map[string]string{"grant_type": "client_credentials"})
  req, _ := http.NewRequest("POST", "${authUrl}", bytes.NewReader(body))
  req.Header.Set("Authorization", "Basic "+creds)
  req.Header.Set("Content-Type", "application/json")
  req.Header.Set("Accept", "application/json")

  resp, err := http.DefaultClient.Do(req)
  if err != nil { panic(err) }
  defer resp.Body.Close()
  raw, _ := io.ReadAll(resp.Body)

  if resp.StatusCode < 200 || resp.StatusCode >= 300 {
    panic(fmt.Errorf("auth failed (%d): %s", resp.StatusCode, string(raw)))
  }

  var tr tokenResponse
  _ = json.Unmarshal(raw, &tr)
  fmt.Println(tr.AccessToken)
}`
        },
        'nodejs-payment': {
            language: 'javascript',
            code: `const axios = require('axios');

async function createPaymentRequest(accessToken) {
  const paymentData = {
    amount: 100.5,
    currency: 'USD',
    ref: 'CMD-2024-001',
    referer_url: 'https://votre-site.com/confirmation',
  };

  const res = await axios.post(
    '${paymentUrl}',
      paymentData,
      {
        headers: {
        Authorization: \`Bearer \${accessToken}\`,
          'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    }
  );

  // res.data.link => URL de paiement
  return res.data;
}`
        },
        'ts-payment': {
            language: 'typescript',
            code: `type PaymentRequest = {
  amount: number;
  currency: 'USD' | 'CDF';
  ref: string;
  referer_url: string;
};

export async function createPayment(accessToken: string, body: PaymentRequest) {
  const res = await fetch('${paymentUrl}', {
    method: 'POST',
    headers: {
      Authorization: \`Bearer \${accessToken}\`,
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error(\`Payment request failed: \${res.status}\`);
  return res.json();
}`
        },
        'python-payment': {
            language: 'python',
            code: `import requests

def create_payment_request(access_token: str):
    payload = {
        "amount": 100.5,
        "currency": "USD",
        "ref": "CMD-2024-001",
        "referer_url": "https://votre-site.com/confirmation",
    }

    res = requests.post(
        "${paymentUrl}",
        json=payload,
        headers={
            "Authorization": f"Bearer {access_token}",
            "Content-Type": "application/json",
            "Accept": "application/json",
        },
        timeout=30,
    )
    res.raise_for_status()
    return res.json()`
        },
        'php-payment': {
            language: 'php',
            code: `<?php
$accessToken = '<access_token>';

$payload = json_encode([
  'amount' => 100.5,
  'currency' => 'USD',
  'ref' => 'CMD-2024-001',
  'referer_url' => 'https://votre-site.com/confirmation',
]);

$ch = curl_init('${paymentUrl}');
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_POST => true,
  CURLOPT_HTTPHEADER => [
    'Authorization: Bearer ' . $accessToken,
    'Content-Type: application/json',
    'Accept: application/json',
  ],
  CURLOPT_POSTFIELDS => $payload,
]);

$raw = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($status < 200 || $status >= 300) {
  throw new Exception("Payment request failed ($status): " . $raw);
}

echo $raw; // contient notamment "link"
?>`
        },
        'go-payment': {
            language: 'go',
            code: `package main

import (
  "bytes"
  "encoding/json"
  "fmt"
  "io"
  "net/http"
)

func main() {
  accessToken := "<access_token>"
  payload := map[string]any{
    "amount": 100.5,
    "currency": "USD",
    "ref": "CMD-2024-001",
    "referer_url": "https://votre-site.com/confirmation",
  }
  body, _ := json.Marshal(payload)

  req, _ := http.NewRequest("POST", "${paymentUrl}", bytes.NewReader(body))
  req.Header.Set("Authorization", "Bearer "+accessToken)
  req.Header.Set("Content-Type", "application/json")
  req.Header.Set("Accept", "application/json")

  resp, err := http.DefaultClient.Do(req)
  if err != nil { panic(err) }
  defer resp.Body.Close()
  raw, _ := io.ReadAll(resp.Body)

  if resp.StatusCode < 200 || resp.StatusCode >= 300 {
    panic(fmt.Errorf("payment request failed (%d): %s", resp.StatusCode, string(raw)))
  }

  fmt.Println(string(raw))
}`
        },
        'nodejs-verification': {
            language: 'javascript',
            code: `const axios = require('axios');

// requestId = identifiant de la demande (retourné dans trans_id ou présent dans l'URL link)
async function getPaymentRequest(accessToken, requestId) {
  const url = \`${apiBaseUrl}/transactions/requests/\${encodeURIComponent(requestId)}\`;
  const res = await axios.get(url, {
    headers: {
      Authorization: \`Bearer \${accessToken}\`,
      Accept: 'application/json',
    },
  });
  return res.data;
}`
        },
        'ts-verification': {
            language: 'typescript',
            code: `// requestId = identifiant de la demande (retourné dans trans_id ou présent dans l'URL link)
export async function getPaymentRequest(accessToken: string, requestId: string) {
  const url = \`${apiBaseUrl}/transactions/requests/\${encodeURIComponent(requestId)}\`;
  const res = await fetch(url, {
    headers: {
      Authorization: \`Bearer \${accessToken}\`,
      Accept: 'application/json',
    },
  });
  if (!res.ok) throw new Error(\`Request failed: \${res.status}\`);
  return res.json();
}`
        },
        'python-verification': {
            language: 'python',
            code: `import requests
from urllib.parse import quote

# request_id = identifiant de la demande (retourné dans trans_id ou présent dans l'URL link)
def get_payment_request(access_token: str, request_id: str):
    url = f"${apiBaseUrl}/transactions/requests/{quote(request_id)}"
    res = requests.get(url, headers={
        "Authorization": f"Bearer {access_token}",
        "Accept": "application/json",
    }, timeout=30)
    res.raise_for_status()
    return res.json()`
        },
        'php-verification': {
            language: 'php',
            code: `<?php
$accessToken = '<access_token>';
$requestId = '<request_id>'; // ID de la demande (trans_id ou segment final du link)
$url = '${apiBaseUrl}/transactions/requests/' . rawurlencode($requestId);

$ch = curl_init($url);
curl_setopt_array($ch, [
  CURLOPT_RETURNTRANSFER => true,
  CURLOPT_HTTPHEADER => [
    'Authorization: Bearer ' . $accessToken,
    'Accept: application/json',
  ],
]);

$raw = curl_exec($ch);
$status = curl_getinfo($ch, CURLINFO_HTTP_CODE);
curl_close($ch);

if ($status < 200 || $status >= 300) {
  throw new Exception("Request failed ($status): " . $raw);
}

echo $raw;
?>`
        },
        'go-verification': {
            language: 'go',
            code: `package main

import (
  "fmt"
  "io"
  "net/http"
  "net/url"
)

// requestID = identifiant de la demande (trans_id ou segment final du link)
func main() {
  accessToken := "<access_token>"
  requestID := "<request_id>"
  u := "${apiBaseUrl}/transactions/requests/" + url.PathEscape(requestID)

  req, _ := http.NewRequest("GET", u, nil)
  req.Header.Set("Authorization", "Bearer "+accessToken)
  req.Header.Set("Accept", "application/json")

  resp, err := http.DefaultClient.Do(req)
  if err != nil { panic(err) }
  defer resp.Body.Close()
  raw, _ := io.ReadAll(resp.Body)

  if resp.StatusCode < 200 || resp.StatusCode >= 300 {
    panic(fmt.Errorf("request failed (%d): %s", resp.StatusCode, string(raw)))
  }

  fmt.Println(string(raw))
}`
        },
        'nodejs-webhook': {
            language: 'javascript',
            code: `// Express.js - réception webhook (exemple)
const express = require('express');
const crypto = require('crypto');

const app = express();
app.use(express.json({ verify: (req, res, buf) => { req.rawBody = buf } }));

function verifySignature(rawBody, signature, timestamp, secret) {
  // Exemple de logique (à aligner avec la méthode exacte Netikash)
  const payloadToSign = timestamp + '.' + rawBody;
  const expected = crypto.createHmac('sha256', secret).update(payloadToSign).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signature));
}

app.post('/webhook/netikash', (req, res) => {
  const signature = req.header('X-Signature') || '';
  const timestamp = req.header('X-Timestamp') || '';
  const ok = verifySignature(req.rawBody.toString(), signature, timestamp, process.env.WEBHOOK_SECRET);

  if (!ok) return res.status(401).send('Invalid signature');

  // traiter l'événement
  // req.body: { event, ref, trx, status }
  return res.status(200).json({ received: true });
});`
        },
        'ts-webhook': {
            language: 'typescript',
            code: `// Express.js + TypeScript (exemple)
import express, { Request, Response } from 'express';
import crypto from 'crypto';

const app = express();
app.use(express.json({
  verify: (req: any, _res, buf) => { req.rawBody = buf; }
}));

function verifySignature(rawBody: string, signature: string, timestamp: string, secret: string) {
  const payloadToSign = \`\${timestamp}.\${rawBody}\`;
  const expected = crypto.createHmac('sha256', secret).update(payloadToSign).digest('hex');
  return crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(signature));
}

app.post('/webhook/netikash', (req: any, res: Response) => {
  const signature = (req.header('X-Signature') || '') as string;
  const timestamp = (req.header('X-Timestamp') || '') as string;
  const ok = verifySignature(req.rawBody.toString(), signature, timestamp, process.env.WEBHOOK_SECRET!);

  if (!ok) return res.status(401).send('Invalid signature');
  return res.status(200).json({ received: true });
});`
        },
        'python-webhook': {
            language: 'python',
            code: `# Flask - réception webhook (exemple)
from flask import Flask, request
import hmac, hashlib, os

app = Flask(__name__)

def verify_signature(raw_body: bytes, signature: str, timestamp: str, secret: str) -> bool:
    payload = (timestamp + "." ).encode() + raw_body
    expected = hmac.new(secret.encode(), payload, hashlib.sha256).hexdigest()
    return hmac.compare_digest(expected, signature)

@app.post("/webhook/netikash")
def netikash_webhook():
    signature = request.headers.get("X-Signature", "")
    timestamp = request.headers.get("X-Timestamp", "")
    raw = request.get_data()
    if not verify_signature(raw, signature, timestamp, os.getenv("WEBHOOK_SECRET", "")):
        return ("Invalid signature", 401)

    payload = request.json  # {event, ref, trx, status}
    return ({"received": True}, 200)`
        },
        'php-webhook': {
            language: 'php',
            code: `<?php
// Webhook PHP (exemple)
$raw = file_get_contents('php://input');
$signature = $_SERVER['HTTP_X_SIGNATURE'] ?? '';
$timestamp = $_SERVER['HTTP_X_TIMESTAMP'] ?? '';
$secret = getenv('WEBHOOK_SECRET');

// Exemple de logique (à aligner avec la méthode exacte Netikash)
$payloadToSign = $timestamp . '.' . $raw;
$expected = hash_hmac('sha256', $payloadToSign, $secret);

if (!hash_equals($expected, $signature)) {
  http_response_code(401);
  echo 'Invalid signature';
  exit;
}

http_response_code(200);
echo json_encode(['received' => true]);
?>`
        },
        'go-webhook': {
            language: 'go',
            code: `package main

import (
  "crypto/hmac"
  "crypto/sha256"
  "encoding/hex"
  "io"
  "net/http"
  "os"
)

func verifySignature(raw []byte, signature, timestamp, secret string) bool {
  payload := append([]byte(timestamp+"."), raw...)
  mac := hmac.New(sha256.New, []byte(secret))
  mac.Write(payload)
  expected := hex.EncodeToString(mac.Sum(nil))
  return hmac.Equal([]byte(expected), []byte(signature))
}

func handler(w http.ResponseWriter, r *http.Request) {
  raw, _ := io.ReadAll(r.Body)
  sig := r.Header.Get("X-Signature")
  ts := r.Header.Get("X-Timestamp")
  secret := os.Getenv("WEBHOOK_SECRET")
  if !verifySignature(raw, sig, ts, secret) {
    w.WriteHeader(http.StatusUnauthorized)
    w.Write([]byte("Invalid signature"))
    return
  }
  w.WriteHeader(http.StatusOK)
  w.Write([]byte(\`{"received":true}\`))
}

func main() {
  http.HandleFunc("/webhook/netikash", handler)
  http.ListenAndServe(":8080", nil)
}`
        }
    }), [authUrl, paymentUrl, apiBaseUrl]);

    const exampleCode = useMemo(() => codeExamples[example.id] || {
        language: 'text',
        code: '// Exemple à venir...'
    }, [example.id, codeExamples]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(exampleCode.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // ignore
        }
    };

    const testInSandbox = () => {
        const endpoint =
            category === 'authentication' ? 'auth' :
            category === 'payment' ? 'payment' :
            category === 'verification' ? 'paymentRequestById' :
            'payment';

        window.dispatchEvent(new CustomEvent('netikash:playground', { detail: { endpoint } }));
        const el = document.getElementById('api-playground');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div id={`example-${example.id}`} className="bg-gray-50 border border-gray-200 rounded-2xl overflow-hidden hover:shadow-md transition-shadow">
            <div
                className="p-4 cursor-pointer"
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <Code className="text-amber-600" />
                        </div>
                        <div>
                            <h3 className="font-semibold text-gray-900">{example.title}</h3>
                            <p className="text-sm text-gray-600">{example.description}</p>
                        </div>
                    </div>
                    <button className="text-gray-400 hover:text-gray-600">
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="border-t border-gray-200">
                    <CodeExample
                        code={exampleCode.code}
                        language={exampleCode.language}
                        showLineNumbers={true}
                    />

                    <div className="p-4 bg-white border-t border-gray-200">
                        <div className="flex space-x-3">
                            <button
                                onClick={copy}
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-amber-500 text-white py-2 px-4 rounded-lg text-sm font-bold hover:bg-amber-600 transition-colors"
                            >
                                {copied ? <CheckCircle fontSize="small" /> : <ContentCopy fontSize="small" />}
                                <span>{copied ? 'Copié' : 'Copier le code'}</span>
                            </button>
                            <button
                                onClick={testInSandbox}
                                className="flex-1 inline-flex items-center justify-center gap-2 border border-gray-300 text-gray-700 py-2 px-4 rounded-lg text-sm font-bold hover:bg-gray-50 transition-colors"
                            >
                                <PlayArrow fontSize="small" />
                                <span>Tester (sandbox)</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ExampleCard;